<template>
  <div class="component-wrapper" :class="screenWakeLockBorder">

    <Speed
      v-if="recording"
      :toggleSpeed="recording"
      :val="currentGeolocation"
    />

    <Distance
      v-if="recording"
      :toggleDistance="recording"
      :val="totalDistance"
    />

    <Timer
      :toggleTimer="recording"
      @timerStopped="onTimerStopped"
    />

    <Header
      class="component-header"
      v-bind="propsForHeaderComponent"
      @toggleComponentStatus="toggleComponent()">
      <template v-slot:notification v-if="recording && debugging">
        <div class="warning p-2 bg-yellow-600 text-yellow-200 text-xs mt-2">
          Warning! On-screen debugging is turned on! Due to high data collection this can cause the app becomse very slow and even non-reactive. It is recommended to turn debugging off, when recording longer routes.
        </div>
      </template>
    </Header>

    <!-- Component Body -->
    <div class="component-body">
      <div class="component-interface flex-row flex-nowrap">

        <div class="new-activity-settings transition transform"
          :class="{
            '-translate-x-full': newActivityStatus === 'finished',
            'translate-x-0': newActivityStatus !== 'finished'
          }">

          <p class="pb-2 select-none pointer-events-none font-bold">Choose your activity:</p>

          <div class="mb-4 w-full">
            <v-select
              class="bg-pink-800 text-pink-200 rounded"
              v-if="allActivityTypes"
              label="title"
              v-model="currentActivityType"
              :options="allActivityTypes"
            ></v-select>
            <div v-else>Loading Activity Types...</div>
          </div>

          <Input v-bind="propsForNewActivityTitleInput" v-model="newActivityTitle" />

          <h3>Current GPS Settings</h3>

          <div class="flex items-center justify-between mt-5 py-3 border-t border-pink-200 pointer-events-none">
            <div class="settings-feature">Allow tracking:</div>
            <div :class="{ 'leading-none md:leading-normal': !trackingAllowed }" class="flex flex-col">
              <span :class="{
                'text-green-600': trackingAllowed,
                'text-red-500 font-semibold': !trackingAllowed }"
                class="ml-auto"
              >{{ trackingAllowedText }}</span>
              <span v-if="!trackingAllowed" class="block md:inline md:ml-2 text-xs text-red-500 mb-1">(enable in "Settings")</span>
            </div>
          </div>

          <ul :class="{ 'opacity-30': !trackingAllowed }">

            <li>
              <div class="w-32 lg:w-48 pr-2 text-xs text-gray-400 uppercase font-bold flex-shrink-0">GPS Access:</div>

              <div class="flex flex-row items-center flex-grow">

                <div class="text-green-600 select-none pointer-events-none" v-if="geolocationPermissionStatus === 'granted'">{{ geolocationPermissionStatus }}</div>

                <svg
                  class="zoom-to-current-location"
                  @click="zoomToCurrentLocation()"
                  v-if="geolocationPermissionStatus === 'granted' && geolocationSettings.enableHighAccuracy && trackingAllowed">
                  <use xlink:href="#current-geolocation" />
                </svg>

                <div v-if="geolocationPermissionStatus !== 'granted'" class="relative flex flex-col text-xs mt-2 select-none">

                  <button
                    @click="handleGeolocationPermission"
                    v-if="geolocationPermissionStatus !== 'denied'"
                    class="mb-2 bg-transparent border border-pink-800 text-pink-800 px-2 py-1 rounded-md transition hover:bg-pink-200"
                    :disabled="!trackingAllowed">
                    Grant GPS Access
                  </button>

                  <div class="mr-2" v-if="geolocationPermissionStatus === 'denied'">
                    Geolocation Access is denied, probably because this site is not on a secure domsin (https).
                  </div>

                </div>

              </div>

            </li>

            <li class="pointer-events-none">
              <div class="settings-feature">High Accuracy:</div>
              <div :class="{ 'leading-none md:leading-normal': !enableHighAccuracyText }">
                <span :class="{
                  'text-green-600': geolocationSettings.enableHighAccuracy,
                  'text-red-500 font-semibold': !geolocationSettings.enableHighAccuracy }"
                >{{ enableHighAccuracyText }}</span>
                <span v-if="!geolocationSettings.enableHighAccuracy" class="block md:inline md:ml-2 text-xs text-red-500 mb-1">(enable in "Settings")</span>
              </div>
            </li>

            <li class="pointer-events-none">
              <div class="settings-feature">Timeout:</div>
              <div class="flex flex-row items-baseline justify-start">
                <span :class="{ 'text-green-600': geolocationSettings.timeout === 5000 }">{{ geolocationSettings.timeout }}ms</span> <span v-if="geolocationSettings.timeout === 5000" class="text-xs text-gray-500 ml-1">(standard)</span>
              </div>
            </li>

            <li :class="{ 'pointer-events-none': !trackingAllowed }">
              <div class="settings-feature">Screen wake-lock:</div>
              <div class="relative flex flex-col items-baseline justify-start">
                <div class="flex flex-row items-center justify-center">
                  <span :class="{ 'text-green-600': this.screenWakeLockAvailability === 'enabled' }">{{ this.screenWakeLockAvailability }}</span>
                  <WakeLockButton />
                </div>
                <span v-if="this.screenWakeLockAvailability === 'disabled'" class="block text-xs text-gray-500 ml-1">It is recommended to enable Screen wake-lock, when you want to record your activity.</span>
              </div>
            </li>

            <li class="border-t border-pink-200 pt-3 mt-3">
              <div class="settings-feature">New Activity Status:</div>
              <div class="relative flex flex-col items-baseline justify-start">{{ newActivityStatus }}</div>
            </li>

            <li>
              <div class="settings-feature">Recording:</div>
              <div class="relative flex flex-col items-baseline justify-start">{{ recording }}</div>
            </li>

          </ul>

        </div>

        <!-- Recording stopped -->
        <div
          class="new-activity-finished transition transform"
          :class="{
            '-translate-x-full': newActivityStatus === 'finished',
            'translate-x-0': newActivityStatus !== 'finished'
          }">

          <p class="pb-3 text-center text-pink-800">Activity stopped</p>

          <ul class="mb-4 lg:mb-8 py-4 border-t border-b border-pink-200">

            <li class="flex flex-row items-center">
              <div class="w-1/2 text-right pr-2 text-xs text-gray-400 uppercase font-bold">Total Time:</div>
              <div class="w-1/2 pl-2">{{ timerObj.display }}</div>
            </li>

            <li class="border-t border-pink-200 pt-3 mt-3">
              <div class="settings-feature">New Activity Status:</div>
              <div class="relative flex flex-col items-baseline justify-start">{{ newActivityStatus }}</div>
            </li>

            <li>
              <div class="settings-feature">Recording:</div>
              <div class="relative flex flex-col items-baseline justify-start">{{ recording }}</div>
            </li>

          </ul>

          <p class="text-gray-600 text-sm text-center">If you want to keep the data of this activity, click "Save", otherwise click "Delete".</p>

        </div>

      </div>
    </div>

    <!-- Component Footer - should be done via xState! -->
    <div class="component-footer">
      <div class="component-footer-wrapper">
        <div class="new-activity-recording-buttons" :class="{ 'transition transform translate-y-16': newActivityStatus === 'finished' }">

          <button
            @click="stopNewActivity()"
            :disabled="lockStartStopButtons || !recording || geolocationPermissionStatus !== 'granted'"
            class="btn btn-danger select-none"
          >Stop</button>

          <button @click="toggleStartStopButtons" :disabled="!recording || geolocationPermissionStatus !== 'granted'">
            <svg class="w-6 h-6" >
              <use xlink:href="#lock-open" v-if="!lockStartStopButtons" class="text-blue-400" />
              <use xlink:href="#lock-closed" v-if="lockStartStopButtons" class="text-yellow-600" />
            </svg>
          </button>

          <button
            @click="startNewActivity()"
            v-if="newActivityStatus !== 'paused' && !recording"
            :disabled="recording || lockStartStopButtons || geolocationPermissionStatus !== 'granted' || newActivityStatus === 'finished' || !geolocationSettings.enableHighAccuracy || !trackingAllowed"
            class="btn btn-success select-none"
          >Start</button>

          <button
            @click="pauseNewActivity()"
            v-if="newActivityStatus === 'started' || newActivityStatus === 'resumed'"
            :disabled="lockStartStopButtons || geolocationPermissionStatus !== 'granted'"
            class="btn btn-info select-none"
          >Pause</button>

          <button
            @click="resumeNewActivity()"
            v-if="newActivityStatus === 'paused'"
            :disabled="lockStartStopButtons || geolocationPermissionStatus !== 'granted'"
            class="btn btn-warning select-none"
          >Resume</button>

        </div>
        <div class="new-activity-save-delete-buttons" :class="{ 'transition transform translate-y-16': newActivityStatus !== 'finished' }">
          <button @click="openModal()" class="btn btn-danger select-none">Delete</button>
          <button @click="saveNewActivity()" class="btn btn-success select-none">Save</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import gsap from 'gsap'
import GeoMoverDB from '@/db/indexeddb'
import ComponentPositioner from '@/utils/ComponentPositioner'
import { EventBus } from '@/eventBus'
import { TimeDate } from '@/utils/TimeDate'

export default {
  name: 'NewActivity',
  components: {
    Distance: () => import('@/components/Distance'),
    Header: () => import('@/components/Header'),
    Input: () => import('@/components/form-elements/Input'),
    Speed: () => import('@/components/Speed'),
    Timer: () => import('@/components/Timer'),
    WakeLockButton: () => import('@/components/WakeLockButton')
  },
  data () {
    return {
      //
      // ----- PROPS TO BE PASSED TO CHILD COMPONENTS
      //
      // Props for the "Header" child component
      propsForHeaderComponent: {
        titleObj: {
          title: '...loading...',
          id: 0
        },
        componentActive: false,
        superHeadline: 'New Activity'
      },
      // Props for the "Input" child component
      propsForNewActivityTitleInput: {
        header: 'Give your activity a title',
        headerAddOn: null,
        remark: '(Optional. If empty, activity type title will be used.)',
        stopHint: 'To stop speech recognition say'
      },
      //
      // ----- PARAMS CHANGED BY CALLS FROM CHILD COMPONENTS
      //
      // Timer: the values will be passed up from the Timer component
      timerObj: {
        seconds: 0,
        display: '00:00:00'
      },
      //
      // ----- PARAMS SPECIFIC TO THIS COMPONENT
      //
      // -- COMPONENT POSITIONER
      componentPositioner: {
        active: false // initial setting required for html above
      },
      // Params to hand over to componentPositioner
      componentPositionerParams: {
        active: false,
        el: '.component-wrapper',
        h: 0,
        header: '.component-header',
        body: '.component-body',
        interface: '.component-interface',
        footer: '.component-footer'
      },
      newActivityStatus: 'idle', // started, finished, paused, resumed
      // v-model for input component
      newActivityTitle: '',
      // Id of new activity in DB, when "start activity" creates the base
      // object that does not yet contain the GPS data
      newActivityID: null,
      // Intermediary NewActivity object that holds relevant data like title etc.
      // which is created, when a recording starts. Helps to handle the
      // "saveNewActivity" process better later on. See data folder for blueprint
      newActivityTmpObj: {},
      errorMsg: '',
      lockStartStopButtons: false,
      // Remarks
      note: 'This application adds extended geoJson data to properties.coordinatesExtended: [lon,lat,altitude,now(),heading,speed,accuracy,altitudeAccuracy,recording,totalDistanceObj].'
    }
  },
  computed: {
    //
    // ----- Props for child components
    //
    // Prop for Distance component
    currentActivityType: {
      get () { return this.$store.getters['activityTypes/getCurrentActivityTypeObj'] },
      set (val) { this.$store.dispatch('activityTypes/setCurrentActivityTypeObj', val) }
    },
    // Keep these values in Vuex, that way it is easier to save the endstate of
    // a route which happens in "NewActivity", not "Container"
    totalDistance: {
      get () {
        return this.$store.getters['newActivity/getTotalDistance']
        // const obj = this.$store.getters['newActivity/getTotalDistance']
        // this.$l.debug('NewActivity', '"newActivity/getTotalDistance": obj =', obj)
        // return obj
      },
      set (obj) {
        // console.log('NewActivity: set totalDistance: obj =', obj)
        this.$store.dispatch('newActivity/setTotalDistance', obj)
      }
    },
    //
    // Global Permissions
    //
    trackingAllowed: {
      get () { return this.$store.getters['settings/getTrackingAllowed'] }
      // set (bool) { this.$store.dispatch('settings/setTrackingAllowed', bool) }
    },
    //
    // ----- GPS
    //
    // TODO: Check if there is a simpler method call (I think there are 2 at the moment)
    geolocationPermissionStatus: {
      get () { return this.$store.getters['settings/getGeolocationPermissionStatus'] }
    },
    //
    // Geolocation object (3rd parameter to navigator.watchPosition)
    //
    geolocationSettings: {
      get () { return this.$store.getters['settings/getGeolocationSettings'] }
    },
    currentGeolocation: {
      get () { return this.$store.getters['geolocation/getCurrentGeolocation'] }
    },
    //
    // Texts that shows up below the new activity window
    //
    enableHighAccuracyText: {
      get () { return this.$store.getters['settings/getEnableHighAccuracy'] ? 'enabled' : 'disabled' }
    },
    trackingAllowedText: {
      get () { return this.$store.getters['settings/getTrackingAllowed'] ? 'enabled' : 'disabled' }
    },
    //
    // ----- Screen WakeLock
    //
    screenWakeLockAvailability: {
      get () { return this.$store.getters['settings/getScreenWakeLockAvailability'] ? 'enabled' : 'disabled' }
    },
    screenWakeLockBorder () {
      return (this.$store.getters['newActivity/getRecording'] && this.$store.getters['settings/getScreenWakeLockAvailability'])
        ? 'ring-4 ring-green-600'
        : ''
    },
    //
    // ----- New Activity Params
    //
    allActivityTypes: {
      get () { return this.$store.getters['activityTypes/getAllActivityTypes'] }
    },
    recording: {
      get () { return this.$store.getters['newActivity/getRecording'] },
      set (bool) { this.$store.dispatch('newActivity/setRecording', bool) }
    },
    //
    // ---- Main menu show/hide
    //      moves the new activity window a bit to the left, when menu is shown
    //
    mainNavStatus: {
      get () { return this.$store.getters['ui/getMainNavStatus'] },
      set (bool) { this.$store.dispatch('ui/toggleMainNavStatus', bool) }
    },
    debugging: {
      get () { return this.$store.getters['settings/getDebugging'] }
    }
  },
  methods: {
    init () {
      this.componentPositioner = new ComponentPositioner(this.componentPositionerParams)
      this.componentPositioner.moveWhenNavIsVisible(this.mainNavStatus)
      // Required to update the props for the Header child component
      this.updatePropsForHeaderChildComponent()
    },
    //
    // ----- HANDLE UI
    //
    toggleStartStopButtons () {
      // this.$l.debug('NewActivity', 'toggleStartStopButtons invoked\nthis.lockStartStopButtons =', this.lockStartStopButtons)
      this.lockStartStopButtons = !this.lockStartStopButtons
    },
    toggleInfo: e => {
      const el = e.target.parentNode.nextSibling

      if (el.getBoundingClientRect().height === 0) {
        gsap.to(el, { duration: 0.3, height: 'auto' })
      }
      else {
        gsap.to(el, { duration: 0.3, height: 0 })
      }
    },
    //
    // ----- HANDLE NEW ACTIVITY
    //
    async startNewActivity () {
      // this.$l.info('NewActivity', 'startNewActivity invoked', this.startNewActivity.name)

      // In order to make the timer start properly, check if geolocation
      // is already available before initiating
      if (navigator.geolocation && this.geolocationPermissionStatus && this.geolocationSettings.enableHighAccuracy) {
        try {
          this.newActivityStatus = 'started'
          this.lockStartStopButtons = true
          this.recording = true

          // Close the new activity window
          this.componentPositioner.active = false
          this.componentPositioner.toggleHeight(this.newActivityStatus)

          /// Keep this, will be implemented later (reverse geocoding)
          // const data = await this.getCompleteGeolocationWithReverseGeocodedAddress()
          // this.startGeoLocData = data

          // Create the basic structure of the NewActivity objects which will be
          // saved to IDB eventually in "saveNewProject"
          this.newActivityTmpObj = {
            title: this.newActivityTitle.string || this.$store.getters['activityTypes/getCurrentActivityTypeObj'].title,
            typeTitle: this.$store.getters['activityTypes/getCurrentActivityTypeObj'].title,
            date: Date.now(),
            durationStartStop: '',
            totalDistance: {},
            id: 0,
            geojson: {
              type: 'FeatureCollection',
              features: [{
                type: 'Feature',
                id: null,
                properties: {
                  coordinatesExtended: null,
                  durationInSeconds: 0,
                  endTime: null,
                  note: this.note,
                  startTime: Date.now(),
                  timerDisplay: this.timerObj.display,
                  title: this.newActivityTitle.string || this.$store.getters['activityTypes/getCurrentActivityTypeObj'].title,
                  totalDistance: {},
                  typeTitle: this.$store.getters['activityTypes/getCurrentActivityTypeObj'].title
                },
                geometry: {
                  type: 'LineString',
                  coordinates: null
                }
              }]
            }
          }
        }
        catch (e) {
          this.$l.error('NewActivity', 'startNewActivity\ntry-catch: error', e.id + ': ' + e.message)
          this.errorStr = e.message
        }
      }
      else {
        this.$l.error('NewActivity', 'startNewActivity', 'Geolocation not availabe denied or enableHighAccuracy not set.')
      }
    },
    stopNewActivity () {
      this.$l.info('NewActivity', 'stopNewActivity invoked', '')

      /// Stop the recording immendiately, but do not yet save it
      this.recording = false
      this.newActivityStatus = 'finished'

      // UI: open component again
      this.componentPositioner.active = true
      this.componentPositioner.toggleHeight(this.newActivityStatus)

      // These infos have to be gathered and saved to the tmpObj immediately.
      // There is other info, that will only be saved, once the user clicks "save"
      this.newActivityTmpObj.geojson.features[0].geometry.coordinates = this.$store.getters['newActivity/getTmpCoords']
      this.newActivityTmpObj.geojson.features[0].properties.coordinatesExtended = this.$store.getters['newActivity/getTmpCoordsExt']
      this.newActivityTmpObj.geojson.features[0].properties.endTime = Date.now()
    },
    async saveNewActivity () {
      this.$l.info('NewActivity', 'saveNewActivity invoked', '')
      // TODO: Show loading animation icon (removed in GeoMoverDB promise below)

      const getLastId = GeoMoverDB.getLastId('trackedRoutes')
      const lastId = await getLastId
      this.newActivityId = lastId + 1

      //
      // ----- SAVE GEOJSON OBJECT (that is contained within the GeoMover data object)
      //

      const tmpFeat = this.newActivityTmpObj.geojson.features[0]
      // ----- Add "id" to geojson object
      tmpFeat.id = this.newActivityId
      // Coordinate Reference System: https://tools.ietf.org/html/rfc7946#section-4
      // As per Appendix this is no longer in use
      // tmpFeat.crs = {}
      // bounding box: https://tools.ietf.org/html/rfc7946#section-5
      // tmpFeat.bbox = {}
      // ----- Timer display and duration
      tmpFeat.properties.durationInSeconds = this.timerObj.seconds
      tmpFeat.properties.durationStartStop = TimeDate.formattedDuration(
        this.newActivityTmpObj.geojson.features[0].properties.startTime,
        this.newActivityTmpObj.geojson.features[0].properties.endTime
      )
      tmpFeat.properties.timerDisplay = this.timerObj.display
      // ----- Distance
      //       Get these values from VueX store, to where MapConatiner.vue's
      //       "stopRecording()" saved them to. Saving those final values to
      //       "properties" makes it a lot easier to retrieve them again
      tmpFeat.properties.totalDistance = this.totalDistance

      // Save the title again, since at this point ("stopRecording") the user
      // could have changed it
      tmpFeat.properties.title = this.newActivityTitle.string || this.$store.getters['activityTypes/getCurrentActivityTypeObj'].title

      //
      // ----- SAVE GEOMOVER DATA (data that "surrounds" the geojson object)
      //
      this.newActivityTmpObj.id = this.newActivityId
      this.newActivityTmpObj.title = this.newActivityTitle.string || this.$store.getters['activityTypes/getCurrentActivityTypeObj'].title
      this.newActivityTmpObj.typeTitle = this.$store.getters['activityTypes/getCurrentActivityTypeObj'].title
      this.newActivityTmpObj.durationInSeconds = this.timerObj.seconds
      this.newActivityTmpObj.durationStartStop = TimeDate.formattedDuration(
        this.newActivityTmpObj.geojson.features[0].properties.startTime,
        this.newActivityTmpObj.geojson.features[0].properties.endTime
      )
      this.newActivityTmpObj.timerDisplay = this.timerObj.display
      this.newActivityTmpObj.totalDistance = this.totalDistance

      // this.$l.debug(
      //  'NewActivity',
      //  'saveNewActivity\nthis.newActivityTmpObj',
      //   JSON.stringify(this.newActivityTmpObj, null, ' ')
      // )

      //
      // ----- Save to DB
      //
      const savedActivity = GeoMoverDB.addOne('trackedRoutes', this.newActivityTmpObj)
      savedActivity
        .then(success => {
          this.$l.success('NewActivity', 'save: success - id of added activity', success)
          // reset tmp obj
          this.newActivityTmpObj = {}
        })
        .catch(error => {
          this.$l.error('NewActivity', 'save: error', error.id + ': ' + error.message)
        })

      // Reset some things
      this.resetUI()
      this.resetTmpData()
      // Loading all tracked Routes happens not here, but in the
      // TrackedRoutes component. So invoke a function in VueX to
      // update the records there
      this.$store.dispatch('trackedRoutes/updateAllTrackedRoutes')

      // TODO: Change UI and move to a new display that shows an overview of activity
      this.$router.push({ name: 'tracked-route', params: { id: this.newActivityId } })
    },
    pauseNewActivity () {
      this.timerPaused = true
      this.recording = 'paused'
      this.newActivityStatus = 'paused'
    },
    resumeNewActivity () {
      this.$l.info('NewActivity', 'resumeNewActivity invoked')
      // is this check really needed? The user could still change the settings during recording, so yes...
      if (navigator.geolocation && this.geolocationPermissionStatus && this.geolocationSettings.enableHighAccuracy) {
        try {
          this.timerPaused = false
          this.recording = true
          this.newActivityStatus = 'resumed'
          this.lockStartStopButtons = true
        }
        catch (error) {
          this.$l.error('NewActivity', 'resumeActivity\ntry-catch error', 'could not resume the current recording of the new activity route, because geolocation settings (highAccuracy or golocationPermissionStatus) have been changed.\nerror =' + error.id + ', ' + error.message)
        }
      }
      else {
        this.$l.fatal('NewActivity', 'resumActivity\nerror:', 'Either geolocation is not availabe or geolocation was denied or enableHighAccuracy was not set.')
      }
    },
    deleteNewActivity () {
      this.$l.warn('NewActivity', 'deleteNewActivity invoked', '')
      // SHOW CONFIRMATION DIALOG?
      this.resetUI()
      this.resetTmpData()
      // What else?
      // – delete current data
      // - zoom out to contrains?
      // - Paint the route on the map?
      this.$router.push({ name: 'new-activity-deleted' })
    },
    //
    // ----- UI
    //
    resetUI () {
      // "resetUI()" is called from "deleteActivity" and "saveActivity"
      this.log = 'NewActivity: resetUi<br>'
      // – reset interface
      this.newActivityStatus = 'idle'
      // this.newActivityTitle = { title: '', id: null }
      this.componentPositioner.active = false
      this.componentPositioner.toggleHeight(this.newActivityStatus)
      // deleteTrack also centers the map and creates a position marker
      EventBus.$emit('deleteTrackLayer')
    },
    resetTmpData () {
      // Remove data from newActivityTmpObj
      this.newActivityTmpObj = null
      // Reset tmpCoords to an empty array
      this.$store.dispatch('newActivity/clearTmpCoords')
    },
    //
    // ----- MODAL INTERACTION
    //
    openModal () {
      // we must pass object params with all the information
      const params = {
        mode: 'confirm',
        title: 'Attention',
        text: 'Are you sure you want to delete this recorded track?<br>This action is not undoable!',
        // we are passing callback method for our confirm button
        onConfirm: () => {
          return this.deleteNewActivity()
        }
      }
      this.$modal.show(params)
    },
    //
    // ----- GEOLOCATION PERMISSION (navigator.permissions)
    //
    handleGeolocationPermission () {
      // this.$l.info('NewActivity', 'handleGeolocationPermission invoked, dispatch to store', '')
      this.$store.dispatch('settings/handleGeolocationPermission')
    },
    // ----- EventBus: signal to MapContainer's function "centerMap"
    zoomToCurrentLocation () {
      EventBus.$emit('centerMap')
    },
    //
    // ---- FUNCTIONS CALLED FROM CHILD COMPONENTS
    //
    // opens / collapses the component, called from child component "Header"
    toggleComponent () {
      this.$l.debug('NewActivity', 'toggleComponent invoked', '')

      // document.body.classList.toggle('show-new-activity')
      this.componentPositioner.active = !this.componentPositioner.active
      this.componentPositioner.toggleHeight(this.newActivityStatus)

      // Update props for "Header" child component (changes "+" to "–" toggle)
      this.updatePropsForHeaderChildComponent()

      // Close main nav, but only when component is about to be closed ...
      if (this.componentPositioner.active === false) {
        // ... and main nav is currently active
        if (this.mainNavStatus && this.recording) {
          this.$store.dispatch('ui/toggleMainNavStatus', false)
          this.$store.dispatch('ui/setActiveMenu', '')
        }
      }
    },
    //
    // ----- Timer Stop: called from child component "Timer"
    //
    onTimerStopped (data) {
      // this.$l.info('NewActivity', 'onTimerStopped invoked\ndata', JSON.stringify(data, null, ' '))
      this.timerObj.seconds = data.seconds
      this.timerObj.display = data.display
    },
    //
    // ----- FUNCTIONS TO CHANGE / UPDATE PROPS FOR COMPONENTS
    //
    updatePropsForHeaderChildComponent () {
      // this.$l.info('NewActivity', 'updatePropsForHeaderChildComponent invoked', '')

      let activityTypeObj = this.currentActivityType

      if (!activityTypeObj) {
        activityTypeObj = {
          title: 'Please choose an activity first...',
          id: ''
        }
      }

      this.propsForHeaderComponent.titleObj = activityTypeObj
      this.propsForHeaderComponent.componentActive = this.componentPositioner.active

      // this.$l.debug('NewActivity', 'updatePropsForHeaderChildComponent\nthis.propsForHeaderComponent', JSON.stringify(this.propsForHeaderComponent, null, ' '))
    },
    /**
     * Catch any potential recorded, but not yet saved route in case the page
     * is reloaded (maybe accidentally). Will be called only if recodording
     * and "not yet saved status" ('newActivitystats !== "idle"') are true.
     * Otherwise reloading the page works as expected.
     *
     * @param {object} e - The window event.
     * @param {(boolean|string)} recordingStatus - A boolean or 'paused', when
     *        recording is paused
     * @param {(boolean|string)} newActivityStatus - A boolean or 'paused', when
     *        recording is paused
     * @param {boolean} saveToCrashStore - A setting that decide whether to save
     *        the interrupeted route or not
     * @param {object} tmpObj - The object with the basic one-time data of the
     *        route (name, type...)
     * @param {array} tmpObj - An array of 2 objects that hold there-occuring
     *        Geolocation and GeolocationExtended data
     */
    beforeUnload: (e, recordingStatus, newActivityStatus, saveToCrashStore, tmpObj, routeObj) => {
      // Only prevent reload, when recording is in process or track was not yet saved.
      if (recordingStatus || !newActivityStatus === 'idle') {
        e.preventDefault()

        // Chrome requires returnValue to be set.
        e.returnValue = ''

        // Save the interrupted recording only, when the "Settings" were activated
        if (saveToCrashStore) {
          // this.$l.warn('NewActivity', 'beforeUnload\nSave current track due to interrupted redcording to "crash" DB')

          GeoMoverDB.addOne('crash', {
            unsavedRoute: routeObj,
            tmpObj: tmpObj,
            comment: 'This entry was created just before the page was unloaded and "saveAccidentallyInterruptedRecording" was set to true in "Settings". A route was in the process of being recorded, a page reload, however, interrupted the recording. This is what could be save.'
          })
        }

        // TODO: If the interrupted recording was saved, but the user nevertheless
        //       chose not to reload the page, we have a data set in the crash Store
        //       now.What shall we do with it?
        //       Also: How do we restore such a crashed route?
      }
    }
  },
  mounted () {
    this.init()

    // Clear any track that could have been shown before
    EventBus.$emit('removeRecordedTrack')

    // Re-assign "this" scope to be used with the beforeunload event
    const that = this

    // Prevent (accidental) reload (esp usefull, when recording)
    window.addEventListener('beforeunload', (e) => {
      that.beforeUnload(
        e,
        that.recording,
        that.newActivityStatus,
        that.$store.getters['settings/getSaveAccidentallyInterruptedRecording'],
        that.newActivityTmpObj,
        [
          that.$store.getters['newActivity/getTmpCoords'],
          that.$store.getters['newActivity/getTmpCoordsExt']
        ]
      )
    })
  },
  beforeDestroy () {
    // Remove manually created event listeners
    window.removeEventListener('beforeunload', this.beforeUnload)
  },
  watch: {
    // Do not use an arrow function here
    // Needed to center the NewActivity window when the mainNav is visible
    mainNavStatus: function (newVal) {
      this.componentPositioner.moveWhenNavIsVisible(newVal)
    },
    currentActivityType: function () {
      this.updatePropsForHeaderChildComponent()
    }
  }
}
</script>

<style lang="postcss" scoped>
.debugger {
  @apply absolute w-full p-3 bg-white rounded-md border-2 border-red-600;
}

.new-activity {
  box-shadow: 0 0 12px 3px rgba(53, 53, 53, 0.2);
  transform: translateY(-2.5rem);
}

/**
 * Remember: The position of the 2 following divs is regulated by flex, which
 * puts them side-by-side. The 2nd div is invisible, because the parent div
 * is set to "width: 100%; overflow:hidden". To move the first div outside of
 * visibility and the 2nd one into visibility, BOTH need a "-transform-x-full"!
 */
.new-activity-settings,
.new-activity-finished {
  /**
   * The following classes need to be set on the html tag itself:
   * transform transition
   */
  @apply relative w-full h-full;
  @apply flex-grow flex-shrink-0;
  @apply p-3 md:p-4 lg:px-6 lg:pt-4 lg:pb-5;
  /* @apply duration-500 delay-500; */
}

/* ----- start. stop, pause, resume, delete buttons ----- */

.new-activity-save-delete-buttons,
.new-activity-recording-buttons {
  @apply absolute w-full duration-500;
  @apply flex flex-row items-center justify-between;
  @apply p-3 md:p-4 lg:p-6;
}

h3 {
  @apply text-lg font-bold text-center;
  @apply select-none leading-snug pointer-events-none;
  @apply mb-2;
}
ul {
  @apply pt-4 border-t border-pink-200;
}
li {
  @apply flex flex-row items-center select-none;
}
li .settings-feature {
  @apply w-32 lg:w-48 pr-2 text-xs text-gray-400 uppercase font-bold flex-shrink-0;
}
.test {
  @apply  border-2 p-2 rounded w-full my-2;
}

.zoom-to-current-location {
  @apply w-5 h-5 ml-2 text-green-600;
  @apply select-none cursor-pointer;
}
.zoom-to-current-location:focus {
  @apply ring-2 ring-offset-pink-800 ring-offset-2 ring-transparent;
}
.zoom-to-current-location:hover {
@apply text-green-800;
}
</style>

<!--
// import { GeoDataProvider } from '@/provider/GeoDataProvider'
      // gyroscope: null,
      // accelerometer: null,

    <Speed
      v-if="recording"
      :toggleSpeed="recording"
      :val="speed"
    />

    <div class="average absolute -top-25 px-2 py-1 bg-white rounded-md">
      {{ averageSpeed }} | Sum means: {{ sumMeans }}
    </div>

  /** KEEP !!!!!! Will be integrated!!
    * ----- Geolocation stuff that should probably be handled
    *       somewhere else
    */
  // async getCompleteGeolocationWithReverseGeocodedAddress (service) {
  //   // TODO: the type of service should come from settings
  //   const type = service || 'nominatim'
  //   const pos = await this.getCoordinates()
  //   const address = await this.getAddress(pos, type)
  //   // GeoDataProvider returns a, for the requirements of this app, uniquly
  //   // formatted address of the current location via a "reverse geolocation" service
  //   const geoData = GeoDataProvider(pos, type, address)
  //   return geoData
  // },
  // async getCoordinates () {
  //   return new Promise((resolve, reject) => {
  //     navigator.geolocation.getCurrentPosition(resolve, reject, this.geoLocationSettings)
  //   })
  // },
  // async getAddress (pos, apiService) {
  //   if (apiService.toLowerCase() === 'nominatim') {
  //     return await this.fetchAddressFromNomatim(pos.coords.longitude, pos.coords.latitude)
  //   }
  //   if (apiService.toLowerCase() === 'here') {
  //     const reverseGeocodingParameters = {
  //       prox: `${pos.coords.latitude},${pos.coords.longitude}`,
  //       mode: 'retrieveAddresses',
  //       maxresults: 1
  //     }
  //     return await this.hereAPI.geocoder.reverseGeocode(
  //       reverseGeocodingParameters
  //       // TODO: check what is going n here, why I cannot influence this!
  //       // res => {
  //       //   const results = res.Response.View[0].Result[0].Location
  //       //   return results
  //       // },
  //       // e => new Error(e)
  //     )
  //   }
  // },
  // TODO: this has to be made dynamic
  // async fetchAddressFromNomatim (lon, lat) {
  //   const response = await fetch(
  //     'https://nominatim.openstreetmap.org/reverse?format=json&lon=' + lon + '&lat=' + lat
  //   )
  //   const jsonObj = await response.json()
  //   return jsonObj
  // },

    <div class="absolute w-full w-98 h-24 debugger -top-48 p-3 rounded-md bg-white border-2 border-red-500 text-gray-700">
      {{ debugtxt }}
    </div>

    //
    // ----- Sensors
    //
    // onChangeUsePedometerOnOff () {
    //   // try {
    //   //   navigator.permissions.query({ name: 'gyroscope' })
    //   //     .then(result => {
    //   //       if (result.state === 'denied') {
    //   //         this.$l.error('NewActivity', 'onChangeUsePedometerOnOff\nPermission to use gyroscope sensor is denied.\nresult.state =', result.state)
    //   //       }
    //   //       // Use the sensor.
    //   //       // console.log('gyroscope permission result.state =', result.state)
    //   //       this.$l.success('NewActivity', 'onChangeUsePedometerOnOff\nRequesting gyroscope permission:\nresult.state =', result.state)
    //   //     })
    //   // }
    //   // catch (error) {
    //   //   this.$l.error('NewActivity', 'onChangeUsePedometerOnOff\nCould not find gyroscope in navigator\'s permissions.\nError', error + '\n' + error.name)
    //   // }
    //   this.$store.dispatch('settings/setUsePedometer', !this.$store.getters['settings/getUsePedometer'])
    // },
    // onChangeSensorsAllowedsOnOff () {
    //   this.$store.dispatch('settings/setSensorsAllowed', !this.$store.getters['settings/getSensorsAllowed'])
    // },

    // sensorsAllowed: {
    //   get () { return this.$store.getters['settings/getSensorsAllowed'] }
    //   // set (bool) { this.$store.dispatch('settings/setsensorsAllowed', bool) }
    // },

    // totalSteps: {
    //   get () { return this.$store.getters['newActivity/getTotalSteps'] }
    //   // set (obj) { this.$store.dispatch('newActivity/setTotalSteps', obj) }
    // },
      //
      // TODO: to be implemented
      //
      // hereAPI: {
      //   apiKey: 'koCGRuzdSzk5HGvywLcmyOexH6hrxPotAEEW9tgVx8A',
      //   platform: null,
      //   geocoder: null,
      //   errorStr: null
      // }

      const allSpeeds = []
      let addSpeeds = 0
      const l = this.$store.getters['newActivity/getTmpCoords'].length

      for (let i = 0; i < l; i++) {
        allSpeeds.push(this.$store.getters['newActivity/getTmpCoords'][i][2])
        addSpeeds += this.$store.getters['newActivity/getTmpCoords'][i][2]
      }

      const sumMeans = allSpeeds / l

      this.averageSpeed = this.average(allSpeeds)

      this.$l.debug('NewActivity', 'stopRecording: averageSpeed =', this.averageSpeed)
      this.$l.debug('NewActivity', 'stopRecording: sumMeans =', this.sumMeans)

    /**
     * Calculate the average of all integer values in an array
     * (In this case we calculate the average speed using the speed value from
     * the geolocation API)
     *
     * @param {Array} nums The array of integers
     * @todo: externalise to a utils class
     */
    average: nums => {
      return nums.reduce((a, b) => (a + b)) / nums.length
    },

    //
    // Pedometer ('gyroscope' sensor)
    //
    // usePedometer: {
    //   get () { return this.$store.getters['settings/getUsePedometer'] },
    //   set (bool) { this.$store.dispatch('settings/setUsePedometer', bool) }
    // },
    // steps: {
    //   get () { return this.$store.getters['settings/getSteps'] },
    //   set (int) { this.$store.dispatch('settings/setSteps', int) }
    // },
    // screenWakeLockBorderForTimer () {
    // Used with ":class="screenWakeLockBorderForTimer"" on Timer"
    // return (this.$store.getters['newActivity/getRecording'] && this.$store.getters['settings/getScreenWakeLockAvailability'])
    // ? 'timer-border'
    // : ''
    // },

    // gyroscopePermissionStatus: {
    //   get () { return this.$store.getters['settings/getGysroscopePermissionStatus'] }
    // },

    speed: {
      get () {
        const obj = this.$store.getters['newActivity/getSpeed']
        console.log('newActivity speed obj =', obj)
        return obj
      },
      set (int) {
        this.$store.dispatch('newActivity/setSpeed', int)
      }
    },
-->
